import { useState, useRef, useCallback, useEffect } from 'react';
import { api, type VocabRule } from '@/lib/api';
import {
  WORKLET_NAME,
  getAudioWorkletBlobUrl,
  float32ToInt16,
  checkAudioSupport,
} from '@/utils/audioProcessor';
import {
  buildCorrectionRules,
  buildDeepgramReplaceParams,
  applyVocabularyCorrections,
  type CorrectionRule,
} from '@/utils/vocabularyCorrection';

export type ConnectionStatus = 'idle' | 'connecting' | 'recording' | 'paused' | 'transcribing';

/** 確定済みセグメント: 確定テキストと確定時刻を持つ */
export interface FinalizedSegment {
  text: string;
  finalizedAt: number; // Date.now() — アニメーション用
}

interface UseDeepgramTranscriberReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  isRecording: boolean;
  isPaused: boolean;
  isTranscribing: boolean;
  /** 確定済みセグメント配列（各セグメントごとにアニメ可能） */
  segments: FinalizedSegment[];
  /** 全確定テキストを結合した文字列（親への通知用） */
  transcript: string;
  streamingText: string;
  /** 現在の発話セグメントの interim テキスト（リアルタイム書き変わり） */
  interimText: string;
  error: string | null;
  duration: number;
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  connectionStatus: ConnectionStatus;
  isNearLimit: boolean;
  maxDuration: number;
  clear: () => void;
}

const DEFAULT_MAX_DURATION_SEC = 600; // 10 min for free users
const WARN_BEFORE_SEC = 30;
const RECONNECT_MAX_RETRIES = 3;
const RECONNECT_DELAY_MS = 2000;
const FINAL_RESULTS_TIMEOUT_MS = 3000;

interface UseDeepgramTranscriberOptions {
  maxDuration?: number; // seconds, 0 = unlimited
}

export const useDeepgramTranscriber = (options?: UseDeepgramTranscriberOptions): UseDeepgramTranscriberReturn => {
  const maxDurationSec = options?.maxDuration ?? DEFAULT_MAX_DURATION_SEC;
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  const [segments, setSegments] = useState<FinalizedSegment[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [isNearLimit, setIsNearLimit] = useState(false);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number>(0);
  const retryCountRef = useRef(0);
  const isStoppingRef = useRef(false);
  const deepgramTokenRef = useRef<string | null>(null);
  const vocabRulesRef = useRef<VocabRule[]>([]);
  const correctionRulesRef = useRef<CorrectionRule[]>([]);
  const audioBufferQueueRef = useRef<ArrayBuffer[]>([]);
  const blobUrlRef = useRef<string | null>(null);
  const MAX_BUFFER_QUEUE = 100;

  const isRecording = connectionStatus === 'recording';
  const isPaused = connectionStatus === 'paused';
  const isTranscribing = connectionStatus === 'transcribing';

  // 全セグメントを結合した transcript（親への通知用）
  const transcript = segments.map(s => s.text).join('');

  // トークン＋ボキャブラリー事前取得（マウント時に1回）
  useEffect(() => {
    if (!deepgramTokenRef.current) {
      api.getDeepgramToken().then(({ token }) => {
        deepgramTokenRef.current = token;
      }).catch((err) => {
        console.warn('[Deepgram] トークン事前取得失敗（録音開始時にリトライ）:', err);
      });
    }
    api.getVocabularyRules().then(({ rules }) => {
      vocabRulesRef.current = rules || [];
      correctionRulesRef.current = buildCorrectionRules(vocabRulesRef.current);
    }).catch((err) => {
      console.warn('[Deepgram] ボキャブラリー取得失敗（なしで動作継続）:', err);
    });
  }, []);

  // Cleanup helpers
  const cleanupAudio = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    analyserRef.current = null;
    audioBufferQueueRef.current = [];
  }, []);

  const cleanupWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
  }, []);

  const cleanupTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Connect to Deepgram
  const connectDeepgram = useCallback(async (sampleRate: number): Promise<WebSocket> => {
    if (!deepgramTokenRef.current) {
      const { token } = await api.getDeepgramToken();
      deepgramTokenRef.current = token;
    }

    const params = new URLSearchParams({
      model: 'nova-3',
      language: 'ja',
      encoding: 'linear16',
      sample_rate: String(sampleRate),
      channels: '1',
      interim_results: 'true',
      endpointing: '300',
      utterance_end_ms: '1000',
      smart_format: 'true',
      punctuate: 'true',
    });

    // Deepgram replace パラメータで辞書ルールをサーバー側置換
    const replaceParams = buildDeepgramReplaceParams(vocabRulesRef.current);
    const urlParams = params.toString() + (replaceParams ? '&' + replaceParams : '');

    const ws = new WebSocket(
      `wss://api.deepgram.com/v1/listen?${urlParams}`,
      ['token', deepgramTokenRef.current]
    );

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        ws.close();
        reject(new Error('Deepgram接続タイムアウト'));
      }, 10000);

      ws.onopen = () => {
        clearTimeout(timeout);
        retryCountRef.current = 0;
        while (audioBufferQueueRef.current.length > 0) {
          const buffer = audioBufferQueueRef.current.shift();
          if (buffer && ws.readyState === WebSocket.OPEN) {
            ws.send(buffer);
          }
        }
        resolve(ws);
      };

      ws.onerror = (event) => {
        clearTimeout(timeout);
        console.error('Deepgram WebSocket error:', event);
        console.error('Deepgram WS readyState:', ws.readyState, 'url:', ws.url);
        reject(new Error('Deepgram接続エラー'));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'Results') {
            const alt = data.channel?.alternatives?.[0];
            if (!alt) return;

            const text = alt.transcript || '';
            if (!text) return;

            if (data.is_final) {
              // 確定: 辞書補正を適用してセグメントとして追加
              const corrected = correctionRulesRef.current.length > 0
                ? applyVocabularyCorrections(text, correctionRulesRef.current)
                : text;
              setSegments(prev => [...prev, { text: corrected, finalizedAt: Date.now() }]);
              setInterimText('');
            } else {
              // 仮: 辞書補正を適用して interimText を上書き
              const corrected = correctionRulesRef.current.length > 0
                ? applyVocabularyCorrections(text, correctionRulesRef.current)
                : text;
              setInterimText(corrected);
            }
          }
        } catch (e) {
          console.error('Deepgram message parse error:', e);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(timeout);
        if (isStoppingRef.current) {
          console.debug('Deepgram WS closed (normal stop)');
          return;
        }
        console.warn('Deepgram WS closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });

        if (retryCountRef.current < RECONNECT_MAX_RETRIES) {
          retryCountRef.current++;
          console.debug(`Deepgram reconnecting (${retryCountRef.current}/${RECONNECT_MAX_RETRIES})...`);
          setTimeout(async () => {
            try {
              const newWs = await connectDeepgram(sampleRate);
              wsRef.current = newWs;
            } catch {
              setError('Deepgram再接続に失敗しました');
            }
          }, RECONNECT_DELAY_MS);
        } else {
          setError('Deepgram接続が切断されました。録音を再開してください。');
          setConnectionStatus('idle');
          cleanupAudio();
          cleanupTimer();
        }
      };
    });
  }, [cleanupAudio, cleanupTimer]);

  const stopRecording = useCallback(() => {
    isStoppingRef.current = true;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CloseStream' }));
    }
    setConnectionStatus('transcribing');
    setStreamingText('最終結果を待機中...');

    setTimeout(() => {
      cleanupWebSocket();
      cleanupAudio();
      cleanupTimer();
      setStreamingText('');
      setInterimText('');
      setConnectionStatus('idle');
      setIsNearLimit(false);
      isStoppingRef.current = false;
      pausedDurationRef.current = 0;
    }, FINAL_RESULTS_TIMEOUT_MS);
  }, [cleanupWebSocket, cleanupAudio, cleanupTimer]);

  const pauseRecording = useCallback(() => {
    if (connectionStatus !== 'recording') return;
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'KeepAlive' }));
    }
    pauseStartRef.current = Date.now();
    setConnectionStatus('paused');
  }, [connectionStatus]);

  const resumeRecording = useCallback(() => {
    if (connectionStatus !== 'paused') return;
    if (pauseStartRef.current > 0) {
      pausedDurationRef.current += Date.now() - pauseStartRef.current;
      pauseStartRef.current = 0;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = (event: MessageEvent) => {
        const float32Data = event.data as Float32Array;
        const int16Data = float32ToInt16(float32Data);
        const buffer = int16Data.buffer as ArrayBuffer;
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(buffer);
        } else if (audioBufferQueueRef.current.length < MAX_BUFFER_QUEUE) {
          audioBufferQueueRef.current.push(buffer);
        } else {
          console.debug('[Deepgram] Audio buffer queue full, dropping buffer');
        }
      };
    }
    setConnectionStatus('recording');
  }, [connectionStatus]);

  const startRecording = useCallback(async () => {
    if (connectionStatus !== 'idle') return;
    setError(null);
    setConnectionStatus('connecting');
    isStoppingRef.current = false;
    retryCountRef.current = 0;
    audioBufferQueueRef.current = [];

    try {
      const support = checkAudioSupport();
      if (!support.getUserMedia) {
        throw new Error('マイクAPIが利用できません');
      }
      if (!support.audioWorklet) {
        throw new Error('AudioWorklet非対応ブラウザです。最新のChrome/Edge/Safariをご利用ください。');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioCtx();
      audioContextRef.current = audioContext;

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const sampleRate = audioContext.sampleRate;
      const source = audioContext.createMediaStreamSource(stream);

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;

      const blobUrl = getAudioWorkletBlobUrl();
      blobUrlRef.current = blobUrl;
      await audioContext.audioWorklet.addModule(blobUrl);
      const workletNode = new AudioWorkletNode(audioContext, WORKLET_NAME, {
        processorOptions: { bufferDurationMs: 20 },
      });
      workletNodeRef.current = workletNode;
      source.connect(workletNode);

      const ws = await connectDeepgram(sampleRate);
      wsRef.current = ws;

      workletNode.port.onmessage = (event: MessageEvent) => {
        const float32Data = event.data as Float32Array;
        const int16Data = float32ToInt16(float32Data);
        const buffer = int16Data.buffer as ArrayBuffer;
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(buffer);
        } else if (audioBufferQueueRef.current.length < MAX_BUFFER_QUEUE) {
          audioBufferQueueRef.current.push(buffer);
        } else {
          console.debug('[Deepgram] Audio buffer queue full, dropping buffer');
        }
      };

      setDuration(0);
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      durationIntervalRef.current = window.setInterval(() => {
        // 一時停止中は現在の一時停止時間も含めて計算（タイマーが進まないようにする）
        const currentPausedMs = pauseStartRef.current > 0
          ? pausedDurationRef.current + (Date.now() - pauseStartRef.current)
          : pausedDurationRef.current;
        const elapsed = Math.floor((Date.now() - startTimeRef.current - currentPausedMs) / 1000);
        setDuration(elapsed);
        if (maxDurationSec > 0) {
          if (elapsed >= maxDurationSec - WARN_BEFORE_SEC) {
            setIsNearLimit(true);
          }
          if (elapsed >= maxDurationSec) {
            stopRecording();
          }
        }
      }, 1000);

      setConnectionStatus('recording');
    } catch (err: any) {
      let errorMessage = 'マイクへのアクセスに失敗しました。';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'マイクへのアクセスが拒否されました。ブラウザの設定でマイクを許可してください。';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'マイクが見つかりません。マイクが接続されているか確認してください。';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'マイクが他のアプリで使用中です。他のアプリを閉じてから再度お試しください。';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      cleanupAudio();
      cleanupWebSocket();
      cleanupTimer();
      setConnectionStatus('idle');
    }
  }, [connectionStatus, connectDeepgram, stopRecording, cleanupAudio, cleanupWebSocket, cleanupTimer]);

  const clear = useCallback(() => {
    setSegments([]);
    setStreamingText('');
    setInterimText('');
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      isStoppingRef.current = true;
      cleanupWebSocket();
      cleanupAudio();
      cleanupTimer();
    };
  }, [cleanupWebSocket, cleanupAudio, cleanupTimer]);

  return {
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    isRecording,
    isPaused,
    isTranscribing,
    segments,
    transcript,
    streamingText,
    interimText,
    error,
    duration,
    analyserRef,
    connectionStatus,
    isNearLimit,
    maxDuration: maxDurationSec,
    clear,
  };
};
